/* eslint-disable react/prop-types */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Anchor,
  ButtonPrimary,
  ButtonSecondary,
  ButtonTertiary,
  Checkbox,
  Heading,
  IconButton,
  IconClose,
  IconTrash,
  Input,
  Label,
} from '@americanexpress/dls-react';
import OverflowItem from '../../OverflowItem/OverflowItem';
import { saveSearchListStyle } from '../../../styles/AdvancedFormSearch/AdvStyleList';

const AdvSaveSearch = ({
  pageData,
  setPageData,
  savedSearchList,
  onItemClick,
  showPopup,
  setShowPopup,
  handleFormReset,
  selectedItem,
  setSelectedItem,
}) => {
  const [searchName, setSearchName] = useState('');
  const [includeDateTime, setIncludeDateTime] = useState(false);
  const showDateTimeCheckbox = pageData?.saveSearchProp?.showSaveDateTimeCheckbox || false;
  const [errorMsg, setErrorMsg] = useState('');
  const [status, setStatus] = useState('default');
  const ModalRef = useRef(null);

  const onClose = () => {
    setStatus('default');
    setSearchName('');
    setErrorMsg('');
    setShowPopup(!showPopup);
  };

  const handleClickOutside = (event) => {
    if (ModalRef.current && !ModalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (!showPopup) return;
    document.addEventListener('mousedown', handleClickOutside, true); // use capture phase
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [onClose]);

  const onItemActionClickHandler = (action, item) => {
    onItemClick(action, item);
    onClose();
  };

  const onSaveButtonClick = () => {
    if (savedSearchList != null && savedSearchList.length === 10) {
      setStatus('error');
      setErrorMsg(
        'You have reached maximum limit of 10 to save your searches. Please delete one or more to proceed.'
      );
      return;
    }
    if (searchName === '') {
      setStatus('error');
      setErrorMsg('Please provide the name');
      return;
    }
    if (savedSearchList.filter((item) => (item.searchName || '').trim().toUpperCase() === (searchName || '').trim().toUpperCase()).length > 0) {
      setStatus('error');
      setErrorMsg('This name already exists');
      return;
    }

    const currentSearch = {};
    pageData.formFields.forEach((field) => {
      currentSearch[field.name] = field.value;
    });
    if (showDateTimeCheckbox && !includeDateTime) currentSearch.startEndDateTime = '' ;

    let setError = false;
    let similarSearch = '';
    savedSearchList.forEach((obj) => {
      let count = 0;
      pageData.formFields.forEach((key) => {
        if (currentSearch[key.name]?.toString().toUpperCase()
          === obj?.metaDataJson[key.name]?.toString().toUpperCase()) {
          // eslint-disable-next-line no-plusplus
          count++;
        }
      });
      if (count === pageData.formFields.length) {
        setError = true;
        similarSearch = obj.searchName;
      }
    });
    if (setError) {
      setStatus('error');
      setErrorMsg(`'${similarSearch}' is similar to the current search, please delete the old search to save the new one!`);
      return;
    }

    handleFormReset();

    currentSearch.searchName = searchName;
    onItemActionClickHandler('SAVE', currentSearch);
    setSelectedItem(null);
  };

  const onItemSelect = (item) => {
    const newPageData = { ...pageData };
    const itemData = item?.metaDataJson || {};
    pageData.formFields.forEach((field) => {
      // eslint-disable-next-line max-len
      newPageData.formFields[field.id].value = Object.prototype.hasOwnProperty.call(itemData, field.name)
        ? itemData[field.name]
        : '';
    });
    newPageData.formFields.forEach((field) => {
      if (field.type === 'multi-select' || field.type === 'multi-selectV2') {
        const value = newPageData.formFields[field.id].value;
        let selectedIds = [];
        if (value && value.includes(',')) {
          selectedIds = value.split(',').map(v => Number.parseInt(v.trim(), 10));
        } else if (value) {
          selectedIds = [Number.parseInt(value, 10)];
        }
        newPageData.formFields[field.id].selectedIds = selectedIds;
      }
    });

    setPageData(newPageData);
    setShowPopup(false);
  };

  return (
    <div id="saveSearch-action-component" style={{ display: 'inline-block'}} ref={ModalRef}>
      <OverflowItem
        labelComponent={(
          <ButtonSecondary
            type="button"
            id="AdvFormActionButtons-saveSearch-form"
            className="col-md-1 margin-responsive-r"
            onClick={onClose}
          >
            Save Search
          </ButtonSecondary>
        )}
        width="350"
        id="item-action-overflowItem"
        isLabelComponentClicked={showPopup}
        isOutsideClick={false}
      >
        <div
          aria-label="Popup Action List"
          id="item-action-navigation"
          css={saveSearchListStyle['.navigation']}
        >
          <div css={saveSearchListStyle['.header']}>
            <Heading
              id="AdvFormSaveSearch-formHeader"
              level={1}
              className="dls-gray-06"
              headingStyle="heading3"
            >
              Saved Searches
            </Heading>
            <IconButton
              style={{ width: '5px', marginLeft: '15px' }}
              id="AdvFormSaveSearch-advSearchClose"
              styleType="tertiary"
              icon={<IconClose size="sm" />}
              onClick={onClose}
            />
          </div>
          <div css={saveSearchListStyle['.listBlock']}>
            {savedSearchList.map((item) => (
              <div
                key={item.id}
                css={{
                  ...saveSearchListStyle['.listBlockItem'],
                  backgroundColor: selectedItem?.id === item.id ? '#ECEDEE' : 'transparent', // Apply grey background if clicked
                }}
              >
                <Anchor
                  theme={{
                    padding: '0px !important',
                    marginLeft: '10px !important',
                    textDecoration: 'none !important',
                    minHeight: 'none !important',
                  }}
                  id={`${item.id}-AdvFormSaveSearch-saveSearchName`}
                  onClick={() => {
                    setSelectedItem({ ...item });
                    onItemSelect(item);
                  }}
                >
                  {item.searchName}
                </Anchor>
                <ButtonTertiary
                  icon={<IconTrash size="sm" />}
                  size="sm"
                  onClick={() => {
                    handleFormReset();
                    onItemActionClickHandler('DELETE', item);
                  }}
                  theme={{
                    color: '#C85250 !important',
                    fill: '#C85250 !important',
                  }}
                  style={{ width: '5px' }}
                  id={`${item.id}-AdvFormSaveSearch-advSearchDelete`}
                />
              </div>
            ))}{savedSearchList.length === 0 ? ' No saved search available' : ''}
          </div>
          <div>
            <Label theme={{ padding: '15px 0px 0px 15px' }}>Save Current Search As</Label>
            <div css={saveSearchListStyle['.navigationItemList']}>
              <Input
                value={searchName}
                id="sav-input-field"
                onChange={(e) => {
                  const trimmed = e.target.value.trim();
                  setSearchName(trimmed.slice(0, 24));
                }}
              />
              {showDateTimeCheckbox && (
                <Checkbox
                  id="save-datetime-checkbox"
                  value={includeDateTime}
                  checked={includeDateTime}
                  label="Include Start & End Date"
                  defaultChecked={false}
                  onChange={() => setIncludeDateTime(!includeDateTime)}
                />
              )}
              {status === 'error' && (
                <Alert
                  css={saveSearchListStyle['.saveSection']}
                  style={{ margin: 'auto' }}
                  id="success-primary-error-alert"
                  status={status}
                  showStateIcon={true}
                  show={true}
                >
                  {errorMsg}
                </Alert>
              )}
              <ButtonPrimary
                id="AdvFormActionButtons-saveSearch-form"
                type="submit"
                aria-label="Save"
                onClick={() => onSaveButtonClick(searchName)}
                theme={saveSearchListStyle['.saveButtonTheme']}
                style={saveSearchListStyle['.saveButton']}
              >
                Save
              </ButtonPrimary>
            </div>
          </div>
        </div>
      </OverflowItem>
    </div>
  );
};

AdvSaveSearch.displayName = 'AdvSaveSearch';

AdvSaveSearch.propTypes = {
  pageData: PropTypes.shape({
    formHeader: PropTypes.string,
    formFields: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
      })
    ),
  }),
  setPageData: PropTypes.func.isRequired,
  savedSearchList: PropTypes.arrayOf(PropTypes.shape({})),
  onItemClick: PropTypes.func.isRequired,
  showPopup: PropTypes.bool.isRequired,
  setShowPopup: PropTypes.func.isRequired,
};

export default AdvSaveSearch;
